import { useEffect, useRef } from 'react';

import { IconRestart } from '@components/icons/restart';
import { showInfo } from '@components/toaster/use-toaster';
import { getScopedTranslate } from '@locale/translate';
import { minutes } from '@utils/milliseconds';

import { config } from 'app-config';

const POLL_INTERVAL = minutes(30);
const ACKNOWLEDGED_VERSION_KEY = 'acknowledged-version';

export function useVersionChecker() {
  const timerRef = useRef<ReturnType<typeof setInterval>>();

  useEffect(() => {
    if (config.MODE === 'development') return;
    const { t } = getScopedTranslate(locale);

    const check = async () => {
      try {
        const res = await fetch(`/version.json?_=${Date.now()}`);
        if (!res.ok) return;
        const { buildDate } = await res.json();
        if (!buildDate || buildDate === config.BUILD_DATE) return;
        if (localStorage.getItem(ACKNOWLEDGED_VERSION_KEY) === buildDate) return;

        showInfo(t('title'), {
          id: 'new-version',
          duration: 0,
          customButtons: [
            {
              text: t('refresh'),
              icon: <IconRestart />,
              onClick: () => {
                localStorage.setItem(ACKNOWLEDGED_VERSION_KEY, buildDate);
                window.location.reload();
              },
            },
          ],
        });
      } catch {
        // Network error — ignore
      }
    };

    timerRef.current = setInterval(check, POLL_INTERVAL);
    return () => clearInterval(timerRef.current);
  }, []);
}

const locale = {
  en: {
    title:
      'A new version of Journeys is ready. Refresh to get the latest features and improvements.',
    refresh: 'Refresh',
  },
};
